import React, { useMemo, useState } from "react";
import { Badge, Form, ListGroup, Modal, Nav } from "react-bootstrap";
import { format, isWeekend, startOfMonth, startOfWeek } from "date-fns";

import Icon from "../Icon";
import { faCheck, faClock } from "@fortawesome/free-solid-svg-icons";
import { Duty } from "../../state/Duty";
import { useData } from "../../storage/accessors";

type PeriodTab = "today" | "week" | "month";

const TAB_LABELS: Record<PeriodTab, string> = {
    today: "Сегодня",
    week: "Неделя",
    month: "Месяц",
};

/** Актуальна ли обязанность в выбранном разрезе */
function isRelevant(duty: Duty, tab: PeriodTab, now: Date): boolean {
    switch (duty.periodicity) {
        case "EVERY_DAY":
            return tab === "today";
        case "WEEKDAYS":
            return tab === "today" && !isWeekend(now);
        case "WEEKENDS":
            return tab === "today" && isWeekend(now);
        case "WEEKLY":
            return tab === "week";
        case "MONTHLY":
            return tab === "month";
        default:
            return false;
    }
}

/** Выполнена ли обязанность в текущем периоде своего разреза */
function isDone(duty: Duty, tab: PeriodTab, bounds: Record<PeriodTab, string>): boolean {
    if (!duty.lastDoneDate) return false;
    return duty.lastDoneDate >= bounds[tab];
}

type Props = {
    date: Date;
    /** Показать обязанности только этой сферы (фильтр колонки) */
    filterPart?: string;
};

/**
 * Компактный блок обязанностей в колонке текущего дня: «Обязанности 3 из 4».
 * Клик открывает модалку со списком и чекбоксами, разбитым на сегодня/неделю/месяц.
 */
export const DayDutiesBlock: React.FC<Props> = ({ date, filterPart = "" }) => {
    const { duties } = useData();
    const [showModal, setShowModal] = useState(false);
    const [tab, setTab] = useState<PeriodTab>("today");

    const now = useMemo(() => new Date(), []);
    const dateStr = format(date, "yyyy-MM-dd");
    const todayStr = format(now, "yyyy-MM-dd");

    // Границы периодов: выполнение засчитывается, если lastDoneDate не раньше начала периода
    const bounds = useMemo<Record<PeriodTab, string>>(
        () => ({
            today: todayStr,
            week: format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
            month: format(startOfMonth(now), "yyyy-MM-dd"),
        }),
        [todayStr]
    );

    const allDuties = useMemo(
        () =>
            Object.values(duties.objects)
                .filter((d) => !filterPart || d.lifepart === filterPart)
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0) || (a.order ?? 0) - (b.order ?? 0)),
        [duties.objects, filterPart]
    );

    const tabCounts = useMemo(() => {
        const result: Record<PeriodTab, { total: number; done: number }> = {
            today: { total: 0, done: 0 },
            week: { total: 0, done: 0 },
            month: { total: 0, done: 0 },
        };
        (Object.keys(result) as PeriodTab[]).forEach((t) => {
            const items = allDuties.filter((d) => isRelevant(d, t, now));
            result[t] = { total: items.length, done: items.filter((d) => isDone(d, t, bounds)).length };
        });
        return result;
    }, [allDuties, bounds, now]);

    const visibleDuties = useMemo(
        () => allDuties.filter((d) => isRelevant(d, tab, now)),
        [allDuties, tab, now]
    );

    // Блок нужен только в колонке сегодняшнего дня и только когда есть обязанности
    if (dateStr !== todayStr || tabCounts.today.total + tabCounts.week.total + tabCounts.month.total === 0) {
        return null;
    }

    const total = tabCounts.today.total + tabCounts.week.total + tabCounts.month.total;
    const done = tabCounts.today.done + tabCounts.week.done + tabCounts.month.done;
    const allDone = done === total;

    const toggle = (duty: Duty) => {
        const checked = isDone(duty, tab, bounds);
        duties.save(duty.uuid, { lastDoneDate: checked ? undefined : todayStr });
    };

    return (
        <>
            <li className="list-group-item py-2">
                <div
                    role="button"
                    onClick={() => setShowModal(true)}
                    className="d-flex align-items-center justify-content-between px-2 py-2 rounded border"
                    style={{
                        cursor: "pointer",
                        backgroundColor: allDone ? "rgba(25, 135, 84, 0.08)" : "rgba(13, 110, 253, 0.06)",
                        borderColor: allDone ? "#198754" : "var(--bs-primary)",
                    }}
                >
                    <span className="d-flex align-items-center gap-2">
                        <Icon icon={faClock} size="sm" style={{ color: allDone ? "#198754" : "var(--bs-primary)" }} />
                        <strong style={{ color: allDone ? "#198754" : "var(--bs-primary)" }}>Обязанности</strong>
                    </span>
                    <Badge bg={allDone ? "success" : "primary"}>{`${done} из ${total}`}</Badge>
                </div>
            </li>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5">Обязанности</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab((k as PeriodTab) || "today")} className="mb-3">
                        {(Object.keys(TAB_LABELS) as PeriodTab[]).map((t) => (
                            <Nav.Item key={t}>
                                <Nav.Link eventKey={t}>
                                    {TAB_LABELS[t]}{" "}
                                    <Badge bg={tabCounts[t].done === tabCounts[t].total ? "success" : "secondary"} className="ms-1">
                                        {tabCounts[t].done}/{tabCounts[t].total}
                                    </Badge>
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>

                    {visibleDuties.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <small>Нет обязанностей на этот период</small>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {visibleDuties.map((duty) => {
                                const checked = isDone(duty, tab, bounds);
                                return (
                                    <ListGroup.Item key={duty.uuid} className="px-0 py-2">
                                        <Form.Check
                                            id={`day-duty-${duty.uuid}`}
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(duty)}
                                            label={
                                                <span className={checked ? "text-decoration-line-through text-success" : ""}>
                                                    {duty.name || "(без названия)"}
                                                </span>
                                            }
                                        />
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <span className="me-auto text-muted">
                        <small>
                            <Icon icon={faCheck} className="me-1" />
                            {done} из {total} выполнено
                        </small>
                    </span>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DayDutiesBlock;
